"use client";

import Link from "next/link";
import ClearProductCompareButton from "./clear-product-compare";
import { useProductCompare } from "@/lib/providers/product-compare-provider";
import { useEffect } from "react";
import { useTranslation } from "@/app/i18n/client";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext, useState } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { getSession } from "next-auth/react";

const ProductCompare = () => {
    const { productCompareList, getProductCompare, getProductSpecificCompare } = useProductCompare();
    const { t, loc } = useTranslation();
    const colorStyle = useContext(ColorStyleContext);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const currentPath = usePathname();
    const [isRender, setIsRender] = useState(false);
    const session = useSession();
    const listPageNotRender = [
        "/product-compare",
        "/checkout",
        "/cart",
        "/payment",
        "/payment-result",
        "/upload-slip",
    ];
    useEffect(() => {
        const _fetchProductCompare = async () => {
            const session = await getSession();
            if (session && session.user) {
                await getProductCompare(); 
                const shouldRender = !listPageNotRender.some(page => currentPath.startsWith(page) || currentPath === page || currentPath.includes(page));
               setIsRender(shouldRender);   
            }
        }
        _fetchProductCompare();
    }, [currentPath]);
 
    useEffect(() => {
        const fetchProductCompare = async () => {
            if (isFirstLoad === false) return; // ป้องกันการเรียกซ้ำ
            setIsFirstLoad(false);
            try {
                const session = await getSession();
                if (session && session.user) {
                    await getProductCompare();   
                }
            } catch (error) {
                console.error("Failed to fetch product compare list:", error);
            }
        };

        fetchProductCompare();
    }, []);

    return (
        productCompareList && productCompareList.length > 0 && isRender && (
            <div
                id="products-compare"
                className="sticky bottom-0 w-full z-9000 border-t border-gray-200 shadow-lg bg-gray-100"
            >
                <div className="bg-gray-100 py-6 flex items-center justify-between w-full px-4 sm:px-10 mx-auto max-w-screen-2xl sm:flex-wrap sm:flex-row flex-col">
                    {/* Left: Instruction */}
                    <div className="text-sm text-gray-600">
                        <p>
                        <span dangerouslySetInnerHTML={{ 
                            __html: t("product.selected_items", { count: productCompareList.length }) 
                        }} /><br />
                        {productCompareList.length === 1 && (
                            <strong>
                                {t("product.add_more")}
                            </strong>
                        )}
                        </p>
                    </div>

                    {/* Middle: Selected Item */}
                    <div className="flex flex-row items-center relative mx-4 overflow-x-auto pt-3 mb-3 w-full sm:w-1/2 sm:justify-center">
                        {productCompareList && productCompareList.length > 0 ? (
                            productCompareList.map((item) => (
                                <div key={item?.msProductId} className="flex items-center space-x-4 mb-2">
                                    <div className="flex items-center justify-center flex-col gap-3 px-2">
                                        <div className="relative w-28 h-28">
                                            <Link
                                                href={`/products/${item.msProduct.handle}`}
                                                className="absolute inset-0 bg-white rounded-lg shadow-lg cursor-pointer flex items-center justify-center overflow-hidden"
                                            >
                                                <img
                                                    src={item?.msProduct?.featuredImageUrl || ""}
                                                    alt={item.msProduct.attributes[0]?.displayName}
                                                    className="w-full h-full max-w-fit max-h-fit"
                                                />
                                            </Link>
                                            <div className="absolute -top-1 -right-1 z-10">
                                                <ClearProductCompareButton itemId={item?.msProductId} />
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-600 w-28 text-ellipsis overflow-hidden text-center">
                                            <span className="truncate w-full block">
                                                {item.msProduct.attributes.find(attr => attr.loc === loc)?.displayName || item.msProduct.attributes[0]?.displayName || t("product.no_name")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <span className="text-sm text-gray-600">{t("product.no_products")}</span>
                        )}
                    </div>

                    {/* Right: Compare Button */}
                    <Link
                        href={`/product-compare`}
                        className="h-14 w-full max-w-[260px] rounded-full dynamic-bg dynamic-color flex items-center justify-center"
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                            } as React.CSSProperties
                        }
                        onClick={() => {getProductSpecificCompare()}}
                    >
                        <div className="flex items-center justify-center gap-2">
                            {t("product.button_compare")}
                        </div>
                    </Link>
                </div>
            </div>
        )
    );
}

export default ProductCompare;